import { useEffect } from "react";

import { useTranslation } from "react-i18next";

import {
  usePlanInvalidation,
  usePlanningRunStatus,
  type PlanningRun,
} from "@/api/planningRun";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";

/** Blocks the page while a run calculates; dismissible only once it failed. */
export function PlanningRunProgressModal({
  run,
  onClose,
  onRetry,
  onSucceeded = onClose,
}: {
  run: PlanningRun | null;
  onClose: () => void;
  onRetry: () => void;
  onSucceeded?: () => void;
}) {
  const { t } = useTranslation();
  const invalidate = usePlanInvalidation();
  const { data } = usePlanningRunStatus(run);
  const state = data?.state;
  const failed = state === "failed";

  useEffect(() => {
    if (run && state === "succeeded") {
      invalidate();
      onSucceeded();
    }
  }, [run, state, invalidate, onSucceeded]);

  const phase = {
    loading: t("planningRuns.progress.phase.loading", "Loading data…"),
    planning: t("planningRuns.progress.phase.planning", "Planning…"),
    persisting: t("planningRuns.progress.phase.persisting", "Saving…"),
  }[data?.phase ?? ""];

  return (
    <Dialog
      open={run !== null}
      onOpenChange={(open) => {
        if (!open && failed) onClose();
      }}
    >
      <DialogContent
        showCloseButton={failed}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => {
          if (!failed) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {t("planningRuns.progress.title", "Running plan")}
          </DialogTitle>
          <DialogDescription>
            {failed
              ? (data?.error ??
                t("planningRuns.progress.failed", "The run failed."))
              : (phase ?? t("planningRuns.progress.starting", "Starting…"))}
          </DialogDescription>
        </DialogHeader>

        {failed ? (
          <DialogFooter>
            <Button onClick={onRetry}>
              {t("planningRuns.progress.retry", "Retry")}
            </Button>
            <Button variant="outline" onClick={onClose}>
              {t("planningRuns.progress.close", "Close")}
            </Button>
          </DialogFooter>
        ) : (
          <Progress value={data?.percent ?? null} />
        )}
      </DialogContent>
    </Dialog>
  );
}
